import SaveAsTemplateButton from '../../components/scribe/SaveAsTemplateButton';
import { ScribeButton as Button, ScribePanel } from '../../components/scribe/ScribeUI';
import { scribeTokens as ui } from '../../components/scribe/tokens';
import React from 'react';
import { Alert, Typography, Box, IconButton, TextField, Badge } from '@mui/material';
import CopyIcon from '@mui/icons-material/FileCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import Tooltip from '@mui/material/Tooltip';
import { CW_UNMETERED_PROVIDERS_ENABLED } from '../../constants/cwUnmeteredProviders.js'; // CW-006 kill-switch (add-speaker-labels-stream / medical-icd-codes)
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { EditorContent } from '@tiptap/react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ConsultationWorkspaceHeader from './ConsultationWorkspaceHeader';

// Shared presentation only. The live container supplies its existing handlers;
// onboarding supplies example data and inert/no-op handlers. No data hooks here.
export default function ConsultationTranscriptPanel({ userDetails, onTemplateSaved, RefiningTranscriptIndicator, RemarksMenuBar, acceptRefinement, applyStreamingSpeakerLabels, canGenerateReport, clearHighlights, consultationTitle, currentHighlightIndex, discardRefinement, exportItem, handleCopy, handleDownloadReport, handleLeftViewChange, highlightCount, highlightedTexts, isPendingRefinement, isRefiningTranscript, isTitleEditing, isLiveTranscribing = false, leftOptions, leftView, compact = false, mobileHidden = false, noSpeechDetected = false, navigateToNextHighlight, navigateToPreviousHighlight, onBeginTitleEdit, onCancelTitleEdit, onGenerateReport, onGenerateHomework, onViewReport, onSaveTitle, sessionDate, onSessionDateChange, refineTranscriptError, refineTranscriptFailure, refinedPreview, remarks, remarksEditor, renderHighlightedTranscription, saveTextOnly, setAlert, setRefinedPreview, setTitleEditValue, setTranscription, sideBySideLeftWidth, textFieldRef, titleEditValue, transcription, transcriptionTextFieldRef }) {
  return (<ScribePanel sx={{ width: compact ? '100%' : { xs: '100%', md: sideBySideLeftWidth }, flexShrink: 0, display: compact ? mobileHidden ? 'none' : 'flex' : { xs: mobileHidden ? 'none' : 'flex', md: 'flex' }, flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                    {consultationTitle !== undefined && <ConsultationWorkspaceHeader title={consultationTitle} editing={isTitleEditing} editValue={titleEditValue} onEditValueChange={setTitleEditValue} onBeginEdit={onBeginTitleEdit} onCancelEdit={onCancelTitleEdit} onSaveTitle={onSaveTitle} onGenerateReport={onGenerateReport} onGenerateHomework={onGenerateHomework} onViewReport={onViewReport} sessionDate={sessionDate} onSessionDateChange={onSessionDateChange} canGenerateReport={canGenerateReport} />}
                    <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${ui.colors.border}`, backgroundColor: ui.colors.subtle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', backgroundColor: ui.colors.subtle, borderRadius: `${ui.radius.control}px`, p: 0.5 }}>
                    {leftOptions.map((option) => (
                      <Button
                        key={option.value}
                        aria-pressed={leftView === option.value}
                        onClick={() => { handleLeftViewChange(option.value); }}
                        size="small"
                        sx={{
                          textTransform: 'none',
                          borderRadius: `${ui.radius.control}px`,
                          padding: '6px 12px',
                          color: leftView === option.value ? ui.colors.text : ui.colors.secondary,
                          fontWeight: 600,
                          fontSize: ui.typography.label.fontSize,
                          backgroundColor: leftView === option.value ? ui.colors.surface : 'transparent',
                          boxShadow: leftView === option.value ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                          '&:hover': {
                            backgroundColor: leftView === option.value ? ui.colors.surface : ui.colors.border,
                          },
                        }}
                      >
                        {option.label}
                      </Button>
                    ))}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {exportItem && <Tooltip title="Export report"><IconButton size="small" onClick={() => handleDownloadReport(exportItem)} sx={{ color: ui.colors.secondary }}><FileDownloadIcon fontSize="small" /></IconButton></Tooltip>}
                        {/* Refine with AI button — visible on Transcription tab when not refining or pending */}
                        {/* Gated by CW_UNMETERED_PROVIDERS_ENABLED — protects the
                            add-speaker-labels-stream Cloud Function (CW-006 owner-ask:
                            sources unlocated, owner not yet asked). Hidden while the
                            flag is false; applyStreamingSpeakerLabels also returns
                            early, so no stream call can fire. */}
                        {leftView === 'Transcription' && !isPendingRefinement && CW_UNMETERED_PROVIDERS_ENABLED && (
                          <Tooltip title="Refine your transcription with AI. You can review the result before saving.">
                            <span>
                              <IconButton
                                size="small"
                                onClick={applyStreamingSpeakerLabels}
                                disabled={!transcription || isRefiningTranscript}
                                sx={{
                                  color: ui.colors.brand,
                                  backgroundColor: ui.colors.brandSoft,
                                  '&:hover': { color: ui.colors.brand, backgroundColor: ui.colors.brandSoft },
                                  '&.Mui-disabled': { color: ui.colors.disabledText, backgroundColor: 'transparent' },
                                }}
                              >
                                <AutoFixHighIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Manual Save button — visible when not pending refinement */}
                        {!isPendingRefinement && (
                          <Tooltip title={`Save ${leftView}`}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Save ${leftView}`}
                                onClick={async () => {
                                  if (await saveTextOnly()) setAlert(`${leftView} saved successfully!`, 'success');
                                }}
                                disabled={isRefiningTranscript}
                                sx={{
                                  color: ui.colors.brand,
                                  backgroundColor: ui.colors.brandSoft,
                                  '&:hover': { color: ui.colors.brand, backgroundColor: ui.colors.brandSoft },
                                  '&.Mui-disabled': { color: ui.colors.disabledText, backgroundColor: 'transparent' },
                                }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Accept / Discard — shown after streaming completes, before save */}
                        {isPendingRefinement && (
                          <>
                            <Button
                              size="small"
                              onClick={() => acceptRefinement()}
                              startIcon={<SaveIcon sx={{ fontSize: '14px !important' }} />}
                              sx={{
                                textTransform: 'none', fontSize: ui.typography.helper.fontSize, fontWeight: 600,
                                color: ui.colors.brand, backgroundColor: ui.colors.brandSoft,
                                border: `1px solid ${ui.colors.brandBorder}`, borderRadius: `${ui.radius.control}px`,
                                px: 1.5, py: 0.4, minWidth: 0,
                                '&:hover': { backgroundColor: ui.colors.brandSoft },
                              }}
                            >
                              Save Changes
                            </Button>
                            <Button
                              size="small"
                              onClick={discardRefinement}
                              startIcon={<UndoIcon sx={{ fontSize: '14px !important' }} />}
                              sx={{
                                textTransform: 'none', fontSize: ui.typography.helper.fontSize, fontWeight: 600,
                                color: ui.colors.secondary, backgroundColor: ui.colors.subtle,
                                border: `1px solid ${ui.colors.border}`, borderRadius: `${ui.radius.control}px`,
                                px: 1.5, py: 0.4, minWidth: 0,
                                '&:hover': { backgroundColor: ui.colors.subtle },
                              }}
                            >
                              Discard
                            </Button>
                          </>
                        )}

                        {/* Copy — always visible */}
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={() => {
                              leftView === 'Transcription' ?
                              handleCopy(`Transcription\n\n` + transcription)
                              :
                              handleCopy(`Manual notes\n\n` + (remarksEditor ? remarksEditor.getText() : remarks))
                            }}
                            sx={{ color: ui.colors.secondary, '&:hover': { color: ui.colors.brand, backgroundColor: ui.colors.brandSoft } }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Left panel content */}
                    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {leftView === 'Transcription' ? (
                  <Box sx={{
                    // padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                    backgroundColor: ui.colors.subtle,
                    borderRadius: `${ui.radius.control}px`,
                  }}>
                    <RefiningTranscriptIndicator
                      isRefining={isRefiningTranscript}
                      hasError={refineTranscriptError}
                      error={refineTranscriptFailure}
                      onRetry={() => applyStreamingSpeakerLabels()}
                      retryDisabled={isRefiningTranscript || isLiveTranscribing || isPendingRefinement || !transcription?.trim()}
                      isPending={isPendingRefinement}
                    />
                    {noSpeechDetected && <Alert severity="warning" sx={{ mb: 1 }}>
                      No speech was detected. Check the audio and transcription language, then try again.
                    </Alert>}
                    {highlightedTexts.length > 0 ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'left', marginBottom: '5px', marginLeft: '16px' }}>
                          <Badge 
                            badgeContent={highlightCount} 
                            sx={{ 
                              marginLeft: '8px', 
                              marginRight: '16px',
                              "& .MuiBadge-badge": {
                                backgroundColor: ui.colors.brand,
                                color: ui.colors.surface
                              }
                            }}
                          />
                          <Tooltip title="Previous highlight">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToPreviousHighlight();
                              }}
                              sx={{ padding: '2px' }}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="caption" sx={{ textAlign: 'center' }}>
                            {currentHighlightIndex}/{highlightCount}
                          </Typography>
                          <Tooltip title="Next highlight">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToNextHighlight();
                              }}
                              sx={{ padding: '2px' }}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button
                            onClick={clearHighlights}
                            sx={{
                              textTransform: 'none',
                              padding: '8px 16px',
                              color: ui.colors.danger,
                              fontWeight: 'bold',
                            }}
                          >
                            Clear Highlights
                          </Button>
                        </Box>
                        <div
                          ref={transcriptionTextFieldRef}
                          style={{
                            ...ui.typography.content,
                            fontFamily: ui.fontFamily,
                            backgroundColor: ui.colors.surface,
                            padding: '16px',
                            borderRadius: `${ui.radius.control}px`,
                            border: `1px solid ${ui.colors.border}`,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            width: '100%',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: renderHighlightedTranscription(transcription || '')
                          }}
                        />
                      </>
                    ) : (
                      <TextField
                        inputRef={textFieldRef}
                        multiline
                        value={(isPendingRefinement || (isRefiningTranscript && refinedPreview)) ? refinedPreview : (transcription ?? '')}
                        onChange={(e) => {
                          if (isPendingRefinement) {
                            setRefinedPreview(e.target.value);
                          } else if (!isRefiningTranscript) {
                            setTranscription(e.target.value);
                          }
                        }}
                        fullWidth
                        placeholder={isLiveTranscribing ? 'Listening… words will appear here as you speak.' : 'Start recording by pressing the start button...'}
                        InputProps={{ readOnly: isRefiningTranscript }}
                        sx={{
                          flex: 1,
                          '& .MuiInputBase-root': {
                            ...ui.typography.content,
                            height: '100%',
                            alignItems: 'flex-start',
                            borderRadius: `${ui.radius.control}px`,
                          },
                          '& .MuiInputBase-input': {
                            height: '100% !important',
                            overflow: 'auto !important'
                          },
                          backgroundColor: ui.colors.surface,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: ui.colors.border },
                            '&:hover fieldset': { borderColor: ui.colors.brand },
                            '&.Mui-focused fieldset': { borderColor: ui.colors.brand },
                          },
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: ui.colors.text,
                            color: ui.colors.text,
                            opacity: 1,
                            '-webkit-text-fill-color': ui.colors.text
                          },
                          '& .MuiOutlinedInput-root.Mui-disabled': {
                            '& > fieldset': { borderColor: ui.colors.border }
                          }
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <Box sx={{
                    backgroundColor: ui.colors.subtle,
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                    borderRadius: `${ui.radius.control}px`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: `${ui.space.sm}px`, mb: 1 }}>
                      <RemarksMenuBar editor={remarksEditor} />
                      <SaveAsTemplateButton userDetails={userDetails} onTemplateSaved={onTemplateSaved} getContent={() => remarksEditor?.getHTML() || remarks || ''} />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        overflow: 'auto',
                        border: `1px solid ${ui.colors.border}`,
                        borderRadius: `${ui.radius.control}px`,
                        backgroundColor: ui.colors.surface,
                        '& .ProseMirror': {
                          ...ui.typography.content,
                          fontFamily: ui.fontFamily,
                          minHeight: '200px',
                          padding: '16px',
                          outline: 'none',
                          '& p': {
                            margin: '0 0 0.5em 0',
                          },
                          '& ul, & ol': {
                            paddingLeft: '1.5em',
                            margin: '0.5em 0',
                          },
                          '& h1': {
                            fontSize: '1.5em',
                            fontWeight: 'bold',
                            margin: '0.5em 0',
                          },
                          '& h2': {
                            fontSize: '1.2em',
                            fontWeight: 'bold',
                            margin: '0.5em 0',
                          },
                        },
                        '& .ProseMirror p.is-editor-empty:first-child::before': {
                          content: '"You can enter any remarks here..."',
                          color: ui.colors.muted,
                          float: 'left',
                          height: 0,
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      <EditorContent editor={remarksEditor} />
                    </Box>
                  </Box>
                )}
                    </Box>
                  </ScribePanel>);
}
